import claimApi from 'Agent/Claims/api/claimApi';
import {Check} from 'Agent/Claims/models/Check';
import {Claim} from 'Agent/Claims/models/Claim';
import {Async, AsyncPage, Empty, Loaded, toPageEither} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import {sortBy, filter} from 'fp-ts/lib/Array';
import {fold, map as mapEither} from 'fp-ts/lib/Either';
import {constant} from 'fp-ts/lib/function';
import {fold as foldOption, fromNullable, isSome, none, Option} from 'fp-ts/lib/Option';
import {getDualOrd, ord, ordDate, ordNumber, ordString} from 'fp-ts/lib/Ord';
import {pipe} from 'fp-ts/lib/pipeable';
import {takeLatest} from 'redux-saga/effects';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {createAction} from 'Shared/utilities/actionCreator';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import {prop} from 'Shared/utilities/prop';
import {getPageApiCallGenerator, PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {ValidAgentNumber} from 'Users/models/AgentNumber';

export enum ClaimActionType {
  GET_CLAIMS = 'claim/GET_CLAIMS',
  CHANGE_CLAIMS = 'claim/CHANGE_CLAIMS',
  CHANGE_CLAIM_FILTERS = 'claim/CHANGE_CLAIM_FILTERS',
  GET_CLAIM_CHECKS = 'claim/GET_CLAIM_CHECKS',
  CHANGE_CLAIM_CHECKS = 'claim/CHANGE_CLAIM_CHECKS',
  CHANGE_SORT = 'claim/CHANGE_SORT',
  CLEAR_SORT = 'claim/CLEAR_SORT',
}

export type FilterEnum = {
  name: string;
  value: string;
};

export const EmptyClaimFilter: ClaimFilters = {
  agentNumber: '',
  name: '',
};

export type ClaimFilters = {
  name: string;
  agentNumber: string;
};

export const changeClaimFilters = (filters: ClaimFilters) =>
  createAction(ClaimActionType.CHANGE_CLAIM_FILTERS, {filters});

export const getClaims = (
  agentNumbers: ValidAgentNumber[],
  isNewYork: boolean,
  encryptedIdentifier: string,
  policyId: string,
  pageOption?: Page<Claim>,
  filter?: ClaimFilters,
  source?: string
) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(ClaimActionType.GET_CLAIMS, {
    ...payload,
    policyId,
    agentNumbers,
    isNewYork,
    encryptedIdentifier,
    source
  });
};
export const changeClaimChecks = (checks: Async<Check[]>) =>
  createAction(ClaimActionType.CHANGE_CLAIM_CHECKS, {checks});
  
export const getClaimChecksPost = (claim: Claim, encryptedIdentifier: string) =>
  createAction(ClaimActionType.GET_CLAIM_CHECKS, {claim, encryptedIdentifier});
export const changeClaims = (claims: AsyncPage<Page<Claim>>) => createAction(ClaimActionType.CHANGE_CLAIMS, claims);
export const clearClaims = () => createAction(ClaimActionType.CHANGE_CLAIMS, DEFAULT_STATE.claims);
export const changeSort = (sort?: Sort) => createAction(ClaimActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(ClaimActionType.CLEAR_SORT);

type ClaimState = {
  policyNumber: string;
  claims: AsyncPage<Page<Claim>>;
  claimsFilter: ClaimFilters;
  checks: Async<Check[]>;
  sort: Option<Sort>;
};

export const DEFAULT_STATE: ClaimState = {
  claims: new Empty(),
  claimsFilter: EmptyClaimFilter,
  policyNumber: '',
  checks: new Empty(),
  sort: none,
};

type ClaimAction =
  | ReturnType<typeof changeClaimChecks>
  | ReturnType<typeof changeClaimFilters>
  | ReturnType<typeof getClaims>
  | ReturnType<typeof changeClaims>
  | ReturnType<typeof clearSort>
  | ReturnType<typeof changeSort>;

export default (state: ClaimState = DEFAULT_STATE, action: ClaimAction): ClaimState => {
  const currentState = state || DEFAULT_STATE;
  switch (action.type) {
    case ClaimActionType.CHANGE_CLAIM_CHECKS:
      return {
        ...currentState,
        checks: action.payload.checks,
      };
    case ClaimActionType.CHANGE_CLAIM_FILTERS:
      return {
        ...currentState,
        claimsFilter: action.payload.filters,
      };
    case ClaimActionType.CHANGE_CLAIMS:
      return {
        ...currentState,
        claims: action.payload,
      };
    case ClaimActionType.CLEAR_SORT:
      return {
        ...currentState,
        sort: DEFAULT_STATE.sort,
      };
    case ClaimActionType.CHANGE_SORT:
      return {
        ...currentState,
        sort: fromNullable(action.payload.sort),
      };
    default:
      return currentState;
  }
};

export const getClaimsFromApi = (action: ReturnType<typeof getClaims>) => {
  return PageFetchAction.match({
    Additional: additionalPage => mergeWithNextPage<Claim>(additionalPage),
    Initial: () =>
      claimApi.getClaims(
        action.payload.agentNumbers.map(prop('value')),
        action.payload.isNewYork,
        action.payload.encryptedIdentifier,
        action.payload.policyId,
        action.payload.filter,
        action.payload.source
      ),
  })(action.payload);
};

export function* watchFetchClaims() {
  yield takeLatest(ClaimActionType.GET_CLAIMS, getPageApiCallGenerator(getClaimsFromApi, changeClaims));
}

export const extractPolicyClaims = (claims: AsyncPage<Page<Claim>>) =>
  pipe(
    toPageEither(claims),
    mapEither(value => new Loaded(value)),
    fold(
      () => undefined,
      value => {
        return value.Data.result;
      }
    )
  )!;

const getComparator = (sortColumn: string) => {
  switch (sortColumn) {
    case 'maskedClaimNumber':
      return ord.contramap(ordString, (claim: Claim) => claim.maskedClaimNumber ?? '');
    case 'claimantName':
      return ord.contramap(ordString, (claim: Claim) => claim.claimantName ?? '');
    case 'claimStatus':
      return ord.contramap(ordString, (claim: Claim) => claim.claimStatus ?? '');
    case 'totalPaidAmount':
      return ord.contramap(ordNumber, (claim: Claim) => claim.totalPaidAmount);
    case 'claimReceiptDate':
      return ord.contramap(ordDate, (claim: Claim) => claim.claimReceiptDate);
    case 'serviceFromDate':
      return ord.contramap(ordDate, (claim: Claim) => claim.serviceFromDate ?? new Date(0));
    case 'serviceThroughDate':
      return ord.contramap(ordDate, (claim: Claim) => claim.serviceThroughDate ?? new Date(0));
    default:
      return ord.contramap(ordDate, (claim: Claim) => claim.claimReceiptDate);
  }
};

export function sortClaims(policyClaimsData: Claim[], sort: Option<Sort>) {
  const {column, direction: sortOrder} = isSome(sort) ? sort.value : {column: 'claimReceiptDate', direction: 'desc'};

  // filter out records in claims array which have a non-null value for sort column
  const nonNullRecordsForSortColumn = pipe(
    policyClaimsData,
    filter(claim => claim[column] !== null)
  );

  // filter out records in claims array which have a null value for sort column
  const nullRecordsForSortColumn = pipe(
    policyClaimsData,
    filter(claim => claim[column] === null)
  );

  if (sortOrder === 'asc') {
    // if sorting in ascending order, put nulls first and sort non-nulls by give sort column
    return [...nullRecordsForSortColumn, ...sortBy([getComparator(column)])(nonNullRecordsForSortColumn)];
  }
  // if sorting in descending order, put nulls last and sort non-nulls by give sort column
  return [...sortBy([getDualOrd(getComparator(column))])(nonNullRecordsForSortColumn), ...nullRecordsForSortColumn];
}
